document.addEventListener('DOMContentLoaded', () => {
  const selectedThickness = document.querySelector('[data-option-slug*="thickness"] input[type="radio"]:checked, [name*="Thickness"]:checked');
  const skipThicknessDepthLogic = shouldSkipThicknessDepthLogic();

  initializeConfigurationFilters();

  // Skip product families that should not use the custom thickness/depth constraints.
  if (!skipThicknessDepthLogic) {
    ensureThicknessDepthAnnouncementElement();
    initializeDirectEventListeners();
    observeVariantPickerRefreshes();
    scheduleThicknessDepthConstraints();
    hideConfigurationOptionLabels();
  }

  let onVariantChangeUnsubscriber = null;

  if (selectedThickness && !skipThicknessDepthLogic) {
    // Subscribe to the variantChange event using Concept's Pub/Sub system
    onVariantChangeUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (eventData) => {
      // Re-initialize direct event listeners after variant refresh if needed
      initializeDirectEventListeners();
      scheduleThicknessDepthConstraints();
      // Handle the initial state based on current selections
      // Display the weight of the selected variant
      displayVariantWeight(eventData);
    });
  } else {
    // Subscribe to the variantChange event using Concept's Pub/Sub system    
    onVariantChangeUnsubscriber = theme.pubsub.subscribe(theme.pubsub.PUB_SUB_EVENTS.variantChange, (eventData) => {
      displayVariantWeight(eventData);
    });
    return;
  }

  // Unsubscribe on page unload
  window.addEventListener('beforeunload', () => {
    if (onVariantChangeUnsubscriber) {
      onVariantChangeUnsubscriber();
      onVariantChangeUnsubscriber = null;
    }
  });
});

let thicknessDepthListenersInitialized = false;
let thicknessDepthObserver = null;
let thicknessDepthConstraintFrame = null;
let thicknessDepthInitialApplied = false;
let thicknessDepthAnnouncementTimeout = null;
let thicknessDepthClearAnnouncementTimeout = null;
let thicknessDepthLastChangedSlug = '';
let thicknessDepthDeselections = [];
const thicknessDepthUnavailableSignature = new Map();
// Last valid (selectable) value per constrained group. Used to revert when a user
// activates a constraint-disabled option, which stays focusable via aria-disabled.
const thicknessDepthLastGoodValue = new Map();

const THICKNESS_DEPTH_ANNOUNCEMENT_ID = 'ThicknessDepthAvailabilityAnnouncement';
const THICKNESS_DEPTH_HINT_ID = 'ThicknessDepthAvailabilityHint';
const THICKNESS_DEPTH_GROUP_DESCRIPTION_ID_PREFIX = 'ThicknessDepthAvailabilityDescription';
const THICKNESS_DEPTH_DISABLED_ATTR = 'data-thickness-depth-disabled';
const THICKNESS_DEPTH_ORIGINAL_DISABLED_ATTR = 'data-thickness-depth-original-disabled';
const THICKNESS_DEPTH_ANNOUNCEMENT_CLEAR_DELAY = 5000;
// Hardcoded English copy (this hint plus the announce*/reject builders below).
// Localization is a known, owner-approved deferred gap (2026-06-25): move these to
// locale-backed `theme.thicknessDepthStrings` via snippets/js-variables.liquid and
// interpolate placeholders in the builders. theme.variantStrings has no key for them
// today. List-joining and is/are pluralization need locale-aware handling at that time.
const THICKNESS_DEPTH_HINT_TEXT = 'Some sizes are unavailable depending on your other selections.';

function shouldSkipThicknessDepthLogic() {
  const excludedTitleKeywords = ['Leif'];
  const excludedTitleEndings = ['Wood Slab'];
  const productTitle = document.querySelector('product-info[data-product-title]')?.dataset.productTitle?.trim().toLowerCase();

  if (!productTitle) return false;

  return (
    excludedTitleKeywords.some(keyword => {
      const normalizedKeyword = keyword.trim().toLowerCase();
      return normalizedKeyword && productTitle.includes(normalizedKeyword);
    }) ||
    excludedTitleEndings.some(ending => {
      const normalizedEnding = ending.trim().toLowerCase();
      return normalizedEnding && productTitle.endsWith(normalizedEnding);
    })
  );
}

/**
 * Initializes direct event listeners for Thickness and Depth input fields.
 */
function initializeDirectEventListeners() {
  if (thicknessDepthListenersInitialized) {
    return;
  }

  // Capture phase + synchronous: the disable/reset state MUST be applied before
  // <variant-picker>'s own bubble-phase change handler reads the checked options and
  // publishes/fetches them — otherwise an invalid Thickness/Depth combination can be
  // published before we reset it (wasted variant fetch + transient invalid state).
  // Listening on `document` (not the option wrapper) keeps this resilient to
  // variant-picker DOM replacement; the capture phase keeps it ahead of
  // variant-picker. Only the live-region text is deferred (see announceThicknessDepth).
  document.addEventListener('change', (event) => {
    const input = event.target;
    const optionWrapper = getThicknessDepthOptionWrapper(input);

    if (input.matches?.('input[type="radio"]') && optionWrapper) {
      // Containment: ignore changes from a customer-favorites active picker. The page
      // can render two pickers (hidden canonical "all-options" + visible "customer-
      // favorites"), and our global selectors would mis-target the hidden canonical
      // and can publish an incomplete selection. Stay inert in that mode (matching the
      // original wrapper-scoped listener) until a favorites-aware scoped pass is built.
      if (input.closest('[data-picker-mode="customer-favorites"]')) return;

      // A constraint-disabled option stays focusable (aria-disabled) so it can be
      // discovered, but it must not be selectable. If one is activated (pointer,
      // arrow key, or AT), revert to the last valid selection, announce why, and stop
      // the event before <variant-picker>'s bubble handler can publish the invalid
      // combination. stopImmediatePropagation from this capture-phase listener halts
      // the event entirely, so variant-picker never sees it.
      if (rejectThicknessDepthSelection(input, optionWrapper)) {
        event.stopImmediatePropagation();
        return;
      }

      // Remember which group the user actually interacted with, so we can announce
      // the *other* group's availability change (the consequence of their action).
      thicknessDepthLastChangedSlug = optionWrapper.getAttribute('data-option-slug') || '';

      // A pending non-interactive pass would only repeat what we run synchronously now.
      if (thicknessDepthConstraintFrame) {
        window.cancelAnimationFrame(thicknessDepthConstraintFrame);
        thicknessDepthConstraintFrame = null;
      }
      runThicknessDepthConstraints();
    }
  }, true);

  thicknessDepthListenersInitialized = true;
}

function getThicknessDepthOptionWrapper(target) {
  const optionWrapper = target?.closest?.('[data-option-slug]');
  const optionSlug = optionWrapper?.getAttribute('data-option-slug') || '';

  return optionSlug.includes('thickness') || optionSlug === 'depth' ? optionWrapper : null;
}

function observeVariantPickerRefreshes() {
  if (thicknessDepthObserver) return;

  const productInfo = document.querySelector('product-info');
  if (!productInfo) return;

  thicknessDepthObserver = new MutationObserver((mutations) => {
    const hasVariantPickerUpdate = mutations.some((mutation) => {
      const addedNodes = Array.from(mutation.addedNodes);
      return addedNodes.some((node) => node.nodeType === Node.ELEMENT_NODE && (
        node.matches?.('variant-picker') || node.querySelector?.('variant-picker')
      ));
    });

    if (hasVariantPickerUpdate) {
      // Re-apply synchronously (not via rAF) the instant the variant-picker DOM is
      // replaced: this runs in the observer's microtask, before paint, so the
      // disabled states are restored before the user can interact with the
      // re-rendered options — closing the one-frame gap where a should-be-disabled
      // option could be clicked and force a missing-value publish.
      runThicknessDepthConstraints();
    }
  });

  thicknessDepthObserver.observe(productInfo, {
    childList: true,
    subtree: true,
  });
}

// Applies the Thickness/Depth disable/reset state, then the (internally deferred)
// hint + announcement work. Runs synchronously for direct user changes (see the
// capture-phase listener) so it beats variant-picker. The rAF wrapper below is only
// for non-interactive triggers: initial load, pub/sub variant change, DOM refresh.
function runThicknessDepthConstraints() {
  thicknessDepthDeselections = [];
  handleInitialState();
  // Invariant: never leave a constrained group with no checked value. If our reset
  // cleared the only selection (e.g. a server-rendered/forbidden combo), select the
  // first still-enabled option in that group and reconcile the cross-group disabling
  // for it. This runs synchronously — before any variant-picker publish — so the
  // submitted option_values set is always complete.
  if (backfillThicknessDepthSelections()) {
    handleInitialState();
  }
  applyThicknessDepthGroupHints();
  announceThicknessDepthChanges();
  recordThicknessDepthLastGoodSelections();
  thicknessDepthInitialApplied = true;
}

// Snapshot each constrained group's currently-checked value. A pass only ever ends in a
// valid state (handleInitialState + backfill guarantee a selectable selection), so this
// is the value to restore to if the user later activates a constraint-disabled option.
function recordThicknessDepthLastGoodSelections() {
  getThicknessDepthGroupWrappers().forEach((wrapper) => {
    const slug = wrapper.getAttribute('data-option-slug') || '';
    const checked = wrapper.querySelector('input[type="radio"]:checked');
    // Only snapshot a value we could legitimately restore to. Guards against recording
    // a constraint-disabled option as "last good" if one is ever transiently checked.
    if (slug && checked && isThicknessDepthSelectable(checked)) {
      thicknessDepthLastGoodValue.set(slug, checked.value);
    }
  });
}

function isThicknessDepthConstraintDisabled(input) {
  return input?.getAttribute?.(THICKNESS_DEPTH_DISABLED_ATTR) === 'true'
    && input.getAttribute(THICKNESS_DEPTH_ORIGINAL_DISABLED_ATTR) !== 'true';
}

// Single source of truth for "can the user choose this option". Since combination
// constraints now use aria-disabled (not the native attribute), neither a natively
// disabled (sold-out) radio nor a constraint-disabled one is selectable. Every
// selected/fallback/last-good decision must use this — not a bare `!radio.disabled`.
function isThicknessDepthSelectable(radio) {
  return !!radio && !radio.disabled && !isThicknessDepthConstraintDisabled(radio);
}

// Returns true if the change was rejected (the option was constraint-disabled). Reverts
// the group to its last valid value and announces the reason via the live region.
function rejectThicknessDepthSelection(input, wrapper) {
  if (!isThicknessDepthConstraintDisabled(input)) return false;

  const slug = wrapper.getAttribute('data-option-slug') || '';
  const radios = Array.from(wrapper.querySelectorAll('input[type="radio"]'));
  const lastGoodValue = thicknessDepthLastGoodValue.get(slug);
  const restore = radios.find((radio) => radio.value === lastGoodValue && isThicknessDepthSelectable(radio))
    || radios.find((radio) => isThicknessDepthSelectable(radio));

  input.checked = false; // programmatic; does not dispatch a change event
  if (restore) restore.checked = true;

  announceThicknessDepth(buildThicknessDepthRejectionMessage(wrapper, input.value));
  return true;
}

function buildThicknessDepthRejectionMessage(wrapper, value) {
  const groupName = getThicknessDepthGroupName(wrapper);
  const causeWrapper = getThicknessDepthCauseWrapper(wrapper);
  const causeName = causeWrapper ? getThicknessDepthGroupName(causeWrapper) : '';
  const causeValue = causeWrapper ? getThicknessDepthSelectedValue(causeWrapper) : '';

  if (!causeName || !causeValue) {
    return `${groupName} ${humanizeThicknessDepthValue(value)} is not selectable.`;
  }

  return `${groupName} ${humanizeThicknessDepthValue(value)} is not selectable with ${causeName} of ${humanizeThicknessDepthValue(causeValue)}.`;
}

function backfillThicknessDepthSelections() {
  let changed = false;

  thicknessDepthDeselections.forEach((reset) => {
    const wrapper = document.querySelector(reset.groupSelector);
    if (!wrapper) return;

    const radios = Array.from(wrapper.querySelectorAll('input[type="radio"]'));
    // Already has a usable selection (shouldn't happen for a radio group, but guard).
    if (radios.some((radio) => radio.checked && isThicknessDepthSelectable(radio))) return;

    const fallback = radios.find((radio) => isThicknessDepthSelectable(radio));
    if (!fallback) return;

    fallback.checked = true; // programmatic; does not dispatch a change event
    reset.fallbackValue = fallback.value;
    changed = true;
  });

  return changed;
}

function scheduleThicknessDepthConstraints() {
  if (thicknessDepthConstraintFrame) {
    window.cancelAnimationFrame(thicknessDepthConstraintFrame);
  }

  thicknessDepthConstraintFrame = window.requestAnimationFrame(() => {
    thicknessDepthConstraintFrame = null;
    runThicknessDepthConstraints();
  });
}

function getThicknessDepthGroupWrappers() {
  return [
    document.querySelector('[data-option-slug*="thickness"]'),
    document.querySelector('[data-option-slug="depth"]'),
  ].filter(Boolean);
}

function getThicknessDepthGroupName(wrapper) {
  // Variant pickers are <fieldset data-option-name> with a <legend>; custom
  // line-item radio groups are a bare <ul data-option-slug> whose name lives on the
  // child inputs (data-option-name). Fall back through all three so an announcement
  // never degrades to the generic "These".
  return wrapper?.getAttribute('data-option-name')
    || wrapper?.querySelector('legend')?.textContent?.trim()
    || wrapper?.querySelector('input[data-option-name]')?.getAttribute('data-option-name')
    || 'These';
}

// Static hint plus current constraint details per group. Re-applied each pass so
// the aria-describedby association survives Shopify variant-picker refreshes.
function applyThicknessDepthGroupHints() {
  const hint = ensureThicknessDepthHintElement();
  getThicknessDepthGroupWrappers()
    .filter((wrapper) => wrapper.querySelector('input[type="radio"]'))
    .forEach((wrapper) => {
      const groupDescription = ensureThicknessDepthGroupDescriptionElement(wrapper);
      const groupDescriptionText = buildThicknessDepthUnavailableMessage(wrapper);
      groupDescription.textContent = groupDescriptionText;

      // Wrapper-level (group) description is only reliably announced on a real semantic
      // radio group (a <fieldset>/<legend>). On a plain <ul role="list"> custom line-item
      // group (e.g. Shelf Thickness) or a <div>+<select> dropdown it is not, so the
      // wrapper-level association is fieldset-only to avoid a silent/misleading hint.
      if (wrapper.tagName === 'FIELDSET') {
        addDescribedBy(wrapper, hint.id);

        if (groupDescriptionText) {
          addDescribedBy(wrapper, groupDescription.id);
        } else {
          removeDescribedBy(wrapper, groupDescription.id);
        }
      }

      // Per-radio describedby is announced on the focusable <input> itself regardless of
      // the container tag, so bind it for constraint-disabled options in *every* group.
      // This is what lets Shelf Thickness — a <ul> line-item group — announce its reason
      // on focus, the same as Depth: a screen reader user arrowing onto a disabled value
      // hears why it's unavailable, not just its label/disabled state.
      wrapper.querySelectorAll('input[type="radio"]').forEach((radio) => {
        if (groupDescriptionText && isThicknessDepthConstraintDisabled(radio)) {
          addDescribedBy(radio, groupDescription.id);
        } else {
          removeDescribedBy(radio, groupDescription.id);
        }
      });
    });
}

// Announce only the consequence of the user's action, at most once per change:
//   1. A reset selection (highest priority — a silently cleared choice is the
//      biggest blocker risk for screen reader users).
//   2. Otherwise a consequence message with the now-unavailable values, such as
//      "Depth 9 inch, 10 inch, 11 inch, and 12 inch are not selectable with
//      Shelf Thickness of 1.25 inch."
function announceThicknessDepthChanges() {
  const groups = getThicknessDepthGroupWrappers().map((wrapper) => {
    const slug = wrapper.getAttribute('data-option-slug') || '';
    const previous = thicknessDepthUnavailableSignature.get(slug);
    const current = getThicknessDepthUnavailableSignature(wrapper);
    thicknessDepthUnavailableSignature.set(slug, current);

    return { wrapper, slug, changed: previous !== undefined && previous !== current };
  });

  // Stay silent on the initial page-load pass; only announce later changes.
  if (!thicknessDepthInitialApplied) return;

  if (thicknessDepthDeselections.length) {
    announceThicknessDepth(buildThicknessDepthDeselectionMessage());
    return;
  }

  const changed = groups.filter((group) => group.changed);
  if (!changed.length) return;

  // Prefer the group the user did NOT just touch (the consequence of their action).
  const consequence = changed.find((group) => group.slug !== thicknessDepthLastChangedSlug) || changed[0];
  const causeWrapper = groups.find((group) => group.wrapper !== consequence.wrapper)?.wrapper;

  announceThicknessDepth(buildThicknessDepthUpdatedMessage(consequence.wrapper, causeWrapper));
}

function getThicknessDepthUnavailableSignature(wrapper) {
  return getThicknessDepthUnavailableValues(wrapper)
    .sort()
    .join('|');
}

function getThicknessDepthUnavailableValues(wrapper) {
  return Array.from(wrapper.querySelectorAll(`input[type="radio"][${THICKNESS_DEPTH_DISABLED_ATTR}="true"]`))
    .map((input) => input.value.trim())
    .filter(Boolean);
}

function buildThicknessDepthUpdatedMessage(wrapper, causeWrapper) {
  const unavailableMessage = buildThicknessDepthUnavailableMessage(wrapper, causeWrapper);
  if (unavailableMessage) return unavailableMessage;

  const groupName = getThicknessDepthGroupName(wrapper);
  const causeName = causeWrapper ? getThicknessDepthGroupName(causeWrapper) : '';
  const causeValue = causeWrapper ? getThicknessDepthSelectedValue(causeWrapper) : '';

  return causeName && causeValue
    ? `All ${groupName} options are selectable with ${causeName} of ${humanizeThicknessDepthValue(causeValue)}.`
    : `${groupName} options updated.`;
}

function buildThicknessDepthUnavailableMessage(wrapper, explicitCauseWrapper) {
  const unavailableValues = getThicknessDepthUnavailableValues(wrapper);
  if (!unavailableValues.length) return '';

  const groupName = getThicknessDepthGroupName(wrapper);
  const causeWrapper = explicitCauseWrapper || getThicknessDepthCauseWrapper(wrapper);
  const causeName = causeWrapper ? getThicknessDepthGroupName(causeWrapper) : '';
  const causeValue = causeWrapper ? getThicknessDepthSelectedValue(causeWrapper) : '';
  const unavailableList = formatThicknessDepthList(unavailableValues.map(humanizeThicknessDepthValue));
  const verb = unavailableValues.length === 1 ? 'is' : 'are';

  if (!causeName || !causeValue) {
    return `${groupName} ${unavailableList} ${verb} not selectable.`;
  }

  return `${groupName} ${unavailableList} ${verb} not selectable with ${causeName} of ${humanizeThicknessDepthValue(causeValue)}.`;
}

function getThicknessDepthCauseWrapper(wrapper) {
  const wrappers = getThicknessDepthGroupWrappers();
  return wrappers.find((groupWrapper) => groupWrapper !== wrapper) || null;
}

function getThicknessDepthSelectedValue(wrapper) {
  return wrapper?.querySelector?.('input[type="radio"]:checked')?.value?.trim() || '';
}

function formatThicknessDepthList(values) {
  if (values.length <= 1) return values[0] || '';
  if (values.length === 2) return `${values[0]} and ${values[1]}`;

  return `${values.slice(0, -1).join(', ')}, and ${values[values.length - 1]}`;
}

function recordThicknessDepthDeselection({ groupSelector, value, causeSelector, causeValue }) {
  thicknessDepthDeselections.push({
    groupSelector,
    groupName: getThicknessDepthGroupName(document.querySelector(groupSelector)),
    value,
    causeName: getThicknessDepthGroupName(document.querySelector(causeSelector)),
    causeValue,
  });
}

function buildThicknessDepthDeselectionMessage() {
  return thicknessDepthDeselections
    .map(({ groupName, value, causeName, causeValue, fallbackValue }) => {
      const reason = `${humanizeThicknessDepthValue(value)} isn't available with ${humanizeThicknessDepthValue(causeValue)} ${causeName.toLowerCase()}`;
      return fallbackValue
        ? `${groupName} changed to ${humanizeThicknessDepthValue(fallbackValue)}; ${reason}.`
        : `${groupName} reset; ${reason}.`;
    })
    .join(' ');
}

function humanizeThicknessDepthValue(value) {
  return (value || '').trim().replace(/"/g, ' inch');
}

function announceThicknessDepth(message) {
  const announcement = ensureThicknessDepthAnnouncementElement();
  announcement.textContent = '';

  if (thicknessDepthAnnouncementTimeout) {
    window.clearTimeout(thicknessDepthAnnouncementTimeout);
    thicknessDepthAnnouncementTimeout = null;
  }

  if (thicknessDepthClearAnnouncementTimeout) {
    window.clearTimeout(thicknessDepthClearAnnouncementTimeout);
    thicknessDepthClearAnnouncementTimeout = null;
  }

  if (!message) return;

  // Brief delay so the cleared region is re-read, then auto-clear so the message
  // doesn't linger in the accessibility tree.
  thicknessDepthAnnouncementTimeout = window.setTimeout(() => {
    announcement.textContent = message;
    thicknessDepthAnnouncementTimeout = null;

    thicknessDepthClearAnnouncementTimeout = window.setTimeout(() => {
      if (announcement.textContent === message) {
        announcement.textContent = '';
      }

      thicknessDepthClearAnnouncementTimeout = null;
    }, THICKNESS_DEPTH_ANNOUNCEMENT_CLEAR_DELAY);
  }, 50);
}

function ensureThicknessDepthAnnouncementElement() {
  let element = document.getElementById(THICKNESS_DEPTH_ANNOUNCEMENT_ID);

  if (!element) {
    element = document.createElement('p');
    element.id = THICKNESS_DEPTH_ANNOUNCEMENT_ID;
    element.className = 'sr-only';
    element.setAttribute('role', 'status');
    element.setAttribute('aria-live', 'polite');
    element.setAttribute('aria-atomic', 'true');

    document.body.appendChild(element);
  }

  return element;
}

function ensureThicknessDepthHintElement() {
  let element = document.getElementById(THICKNESS_DEPTH_HINT_ID);

  if (!element) {
    element = document.createElement('p');
    element.id = THICKNESS_DEPTH_HINT_ID;
    element.className = 'sr-only';
    (document.querySelector('product-info') || document.body).appendChild(element);
  }

  element.textContent = THICKNESS_DEPTH_HINT_TEXT;
  return element;
}

function ensureThicknessDepthGroupDescriptionElement(wrapper) {
  const slug = wrapper.getAttribute('data-option-slug') || 'group';
  const id = `${THICKNESS_DEPTH_GROUP_DESCRIPTION_ID_PREFIX}-${slug.replace(/[^a-z0-9_-]/gi, '-')}`;
  let element = document.getElementById(id);

  if (!element) {
    element = document.createElement('p');
    element.id = id;
    element.className = 'sr-only';
    // A <ul> may only contain <li>, so for a line-item group append next to the list
    // rather than inside it. aria-describedby resolves the IDREF globally, so the
    // association works wherever the element lives.
    const container = wrapper.tagName === 'UL' ? (wrapper.parentElement || wrapper) : wrapper;
    container.appendChild(element);
  }

  return element;
}

function addDescribedBy(element, id) {
  const describedBy = (element.getAttribute('aria-describedby') || '').split(/\s+/).filter(Boolean);
  if (describedBy.includes(id)) return;

  describedBy.push(id);
  element.setAttribute('aria-describedby', describedBy.join(' '));
}

function removeDescribedBy(element, id) {
  const describedBy = (element.getAttribute('aria-describedby') || '')
    .split(/\s+/)
    .filter((describedById) => describedById && describedById !== id);

  if (describedBy.length) {
    element.setAttribute('aria-describedby', describedBy.join(' '));
  } else {
    element.removeAttribute('aria-describedby');
  }
}

function setThicknessDepthOptionConstraintDisabled(option, disabled) {
  if (disabled) {
    if (option.getAttribute(THICKNESS_DEPTH_DISABLED_ATTR) !== 'true') {
      if (option.disabled) {
        option.setAttribute(THICKNESS_DEPTH_ORIGINAL_DISABLED_ATTR, 'true');
      } else {
        option.removeAttribute(THICKNESS_DEPTH_ORIGINAL_DISABLED_ATTR);
      }
    }

    // Combination constraint: mark as unavailable with aria-disabled rather than the
    // native `disabled` attribute, so the radio stays in the focus/arrow-key order and
    // screen reader users in forms mode can reach it and hear the reason (carried by the
    // per-group aria-describedby). Selection is blocked in the capture-phase change
    // listener (see rejectThicknessDepthSelection). A genuinely sold-out option — one
    // that arrived natively disabled from Liquid — stays natively disabled.
    option.disabled = option.getAttribute(THICKNESS_DEPTH_ORIGINAL_DISABLED_ATTR) === 'true';
    option.setAttribute('aria-disabled', 'true');
    // Keep the sighted "unavailable" cue. The swatch/label styling keys off
    // `:disabled, .disabled` (theme.css), so without native `disabled` we drive it
    // with the class instead.
    option.classList.add('disabled');
    option.setAttribute(THICKNESS_DEPTH_DISABLED_ATTR, 'true');
    return;
  }

  if (option.getAttribute(THICKNESS_DEPTH_DISABLED_ATTR) !== 'true') return;

  const wasOriginallyDisabled = option.getAttribute(THICKNESS_DEPTH_ORIGINAL_DISABLED_ATTR) === 'true';
  option.disabled = wasOriginallyDisabled;

  if (wasOriginallyDisabled) {
    option.setAttribute('aria-disabled', 'true');
  } else {
    option.removeAttribute('aria-disabled');
    // Only clear the visual cue we added for the constraint; a server sold-out option
    // keeps the `.disabled` class it was rendered with.
    option.classList.remove('disabled');
  }

  option.removeAttribute(THICKNESS_DEPTH_DISABLED_ATTR);
  option.removeAttribute(THICKNESS_DEPTH_ORIGINAL_DISABLED_ATTR);
}

/**
 * Handles changes to the Thickness input fields.
 */
function handleThicknessChange(thicknessInput) {
  const selectedValue = thicknessInput.value;
  const depthOptions = document.querySelectorAll('[data-option-slug="depth"] input[type="radio"]');

  if (selectedValue === '1.25"' && thicknessInput.checked) {
    depthOptions.forEach(option => {
      if (['9"', '10"', '11"', '12"'].includes(option.value)) {
        setThicknessDepthOptionConstraintDisabled(option, true);
        if (option.checked) {
          option.checked = false;
          recordThicknessDepthDeselection({
            groupSelector: '[data-option-slug="depth"]',
            value: option.value,
            causeSelector: '[data-option-slug*="thickness"]',
            causeValue: selectedValue,
          });
        }
      }
    });
  } else if (selectedValue === '1.5"') {
    depthOptions.forEach(option => {
      if (['11"', '12"'].includes(option.value)) {
        setThicknessDepthOptionConstraintDisabled(option, true);
        if (option.checked) {
          option.checked = false;
          recordThicknessDepthDeselection({
            groupSelector: '[data-option-slug="depth"]',
            value: option.value,
            causeSelector: '[data-option-slug*="thickness"]',
            causeValue: selectedValue,
          });
        }
      } else {
        setThicknessDepthOptionConstraintDisabled(option, false);
      }
    });
  }
  else {
    depthOptions.forEach(option => {
      setThicknessDepthOptionConstraintDisabled(option, false);
    });
  }
}

/**
 * Handles changes to the Depth input fields.
 */
function handleDepthChange(event) {
  const selectedInput = event.target;
  const selectedValue = selectedInput.value;
  const thicknessOptions = document.querySelectorAll('[data-option-slug*="thickness"] input[type="radio"], [name="thickness"]');

  if (['9"', '10"'].includes(selectedValue) && selectedInput.checked) {
    thicknessOptions.forEach(option => {
      if (option.value === '1.25"') {
        setThicknessDepthOptionConstraintDisabled(option, true);
        if (option.checked) {
          option.checked = false;
          recordThicknessDepthDeselection({
            groupSelector: '[data-option-slug*="thickness"]',
            value: option.value,
            causeSelector: '[data-option-slug="depth"]',
            causeValue: selectedValue,
          });
        }
      } else if (option.value === '1.5"' && !option.checked) {
        setThicknessDepthOptionConstraintDisabled(option, false);
      }
    });
  } else if (['11"', '12"'].includes(selectedValue) && selectedInput.checked) {
    thicknessOptions.forEach(option => {
      if (option.value === '1.25"' || option.value === '1.5"') {
        setThicknessDepthOptionConstraintDisabled(option, true);
        if (option.checked) {
          option.checked = false;
          recordThicknessDepthDeselection({
            groupSelector: '[data-option-slug*="thickness"]',
            value: option.value,
            causeSelector: '[data-option-slug="depth"]',
            causeValue: selectedValue,
          });
        }
      }
    });
  } else {
    thicknessOptions.forEach(option => {
      setThicknessDepthOptionConstraintDisabled(option, false);
    });
  }
}

/**
 * Handles the initial state based on current selections.
 */
function handleInitialState() {
  const selectedThickness = document.querySelector('[data-option-slug*="thickness"] input[type="radio"]:checked, [name*="Thickness"]:checked');

  if (selectedThickness) {
    handleThicknessChange(selectedThickness);
  }

  const selectedDepth = document.querySelector('[data-option-slug="depth"] input[type="radio"]:checked, [name*="Depth"]:checked');

  if (selectedDepth) {
    handleDepthChange({ target: selectedDepth });
  }
}

// Display the weight of the selected variant
function displayVariantWeight(eventData) {
  const productId = eventData.data.productId;
  const weightElement = document.querySelector(`#ProductWeight-${productId} span`);
  if (!weightElement || !eventData.data.variant) return;

  const weight = convertToPounds(eventData.data.variant.weight);
  weightElement.textContent = weight.toFixed(2) + " lb";
}

function convertToPounds(weight) {
  return weight * 0.00220462;
}

/**
 * Returns whether a swatch list item is actually rendered (filter inline style,
 * customer-favorites CSS, ancestor hidden, etc.).
 */
function isConfigurationSwatchItemVisible(item) {
  if (!item?.isConnected) {
    return false;
  }

  if (typeof item.checkVisibility === 'function') {
    return item.checkVisibility({
      checkOpacity: true,
      checkVisibilityCSS: true,
    });
  }

  let element = item;
  while (element && element !== document.body) {
    if (element.hidden || element.getAttribute('aria-hidden') === 'true') {
      return false;
    }

    const style = window.getComputedStyle(element);
    if (style.display === 'none' || style.visibility === 'hidden' || style.opacity === '0') {
      return false;
    }

    element = element.parentElement;
  }

  return true;
}

function getEligibleConfigurationSwatchInput(item) {
  const input = item.querySelector('input[type="radio"]');
  if (!input || input.disabled || !isConfigurationSwatchItemVisible(item)) {
    return null;
  }

  return input;
}

/**
 * Radio groups only expose the checked input in the tab order. When filtering hides
 * the checked swatch, keyboard users cannot reach any option until selection moves
 * to a visible, enabled swatch.
 */
function syncConfigurationFilterSelection(formContainer) {
  const eligibleInputs = Array.from(formContainer.querySelectorAll('.swatches li'))
    .map(getEligibleConfigurationSwatchInput)
    .filter(Boolean);

  if (eligibleInputs.length === 0) {
    return;
  }

  const checkedInput = formContainer.querySelector('.swatches input[type="radio"]:checked');
  const checkedIsEligible =
    checkedInput &&
    !checkedInput.disabled &&
    isConfigurationSwatchItemVisible(checkedInput.closest('li'));

  if (checkedIsEligible) {
    return;
  }

  const fallbackInput = eligibleInputs[0];
  if (fallbackInput === checkedInput) {
    return;
  }

  fallbackInput.checked = true;
  fallbackInput.dispatchEvent(new Event('change', { bubbles: true }));
}

function applyConfigurationFilterVisibility(formContainer, activeFilters) {
  const swatchItems = formContainer.querySelectorAll('.swatches li');

  if (activeFilters.length === 0) {
    swatchItems.forEach((item) => {
      item.style.display = '';
    });
    syncConfigurationFilterSelection(formContainer);
    return;
  }

  swatchItems.forEach((item) => {
    const isMatch = activeFilters.some((activeFilter) => item.classList.contains(activeFilter));
    item.style.display = isMatch ? '' : 'none';
  });

  syncConfigurationFilterSelection(formContainer);
}

function isConfigurationFilterButtonEligible(button) {
  if (!button?.isConnected || button.disabled) {
    return false;
  }

  const item = button.closest('li');
  if (item?.classList.contains('customer-favorites-option-hidden')) {
    return false;
  }

  if (item?.style.display === 'none' || button.style.display === 'none') {
    return false;
  }

  const style = window.getComputedStyle(button);
  if (style.display === 'none' || style.visibility === 'hidden') {
    return false;
  }

  return button.offsetParent !== null || style.position === 'fixed';
}

function getConfigurationFilterGroupButtons(group) {
  return Array.from(group.querySelectorAll('.filter-config button[data-filter]'));
}

function getEligibleConfigurationFilterButtons(group) {
  return getConfigurationFilterGroupButtons(group).filter(isConfigurationFilterButtonEligible);
}

function focusConfigurationFilterButton(targetButton) {
  targetButton.focus();
}

function handleConfigurationFilterKeydown(event, group) {
  const navigationKeys = ['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Home', 'End'];
  if (!navigationKeys.includes(event.key)) {
    return;
  }

  const eligible = getEligibleConfigurationFilterButtons(group);
  if (eligible.length === 0) {
    return;
  }

  const currentIndex = eligible.indexOf(event.currentTarget);
  if (currentIndex === -1) {
    return;
  }

  event.preventDefault();

  let nextIndex = currentIndex;
  if (event.key === 'ArrowRight' || event.key === 'ArrowDown') {
    nextIndex = (currentIndex + 1) % eligible.length;
  } else if (event.key === 'ArrowLeft' || event.key === 'ArrowUp') {
    nextIndex = currentIndex === 0 ? eligible.length - 1 : currentIndex - 1;
  } else if (event.key === 'Home') {
    nextIndex = 0;
  } else if (event.key === 'End') {
    nextIndex = eligible.length - 1;
  }

  focusConfigurationFilterButton(eligible[nextIndex]);
}

function toggleConfigurationFilter(filterButton) {
  const filter = filterButton.getAttribute('data-filter');
  const formContainer = filterButton.closest('.product-form__input');

  if (!filter || !formContainer) {
    return;
  }

  let activeFilters = formContainer.activeFilters || [];
  const isActive = activeFilters.includes(filter);

  if (isActive) {
    activeFilters = activeFilters.filter((activeFilter) => activeFilter !== filter);
    filterButton.setAttribute('aria-pressed', 'false');
  } else {
    activeFilters.push(filter);
    filterButton.setAttribute('aria-pressed', 'true');
  }

  formContainer.activeFilters = activeFilters;
  applyConfigurationFilterVisibility(formContainer, activeFilters);

  announceConfigurationFilterChange(formContainer, activeFilters);
}

const CONFIGURATION_FILTER_ANNOUNCE_DELAY = 1200;
const configurationFilterAnnounceTimers = new WeakMap();

// Shelfology Custom: announce configuration filter results for screen readers (WCAG 4.1.3).
// Delay the status text until after VoiceOver finishes the focused toggle ("selected").
// Keep the message visible; Safari skips clipped sr-only live regions.
function announceConfigurationFilterChange(formContainer, activeFilters) {
  const status = formContainer.querySelector('[data-configuration-filter-status]');
  if (!status) {
    return;
  }

  const previousTimers = configurationFilterAnnounceTimers.get(status);
  if (previousTimers) {
    window.clearTimeout(previousTimers.setTimeoutId);
  }

  const message = buildConfigurationFilterStatusMessage(formContainer, status, activeFilters);
  if (!message) {
    status.textContent = '';
    configurationFilterAnnounceTimers.delete(status);
    return;
  }

  const timers = { setTimeoutId: null };
  timers.setTimeoutId = window.setTimeout(() => {
    if (status.textContent === message) {
      status.textContent = '';
    }

    status.textContent = message;
    timers.setTimeoutId = null;
    configurationFilterAnnounceTimers.delete(status);
  }, CONFIGURATION_FILTER_ANNOUNCE_DELAY);

  configurationFilterAnnounceTimers.set(status, timers);
}

function buildConfigurationFilterStatusMessage(formContainer, status, activeFilters) {
  const items = Array.from(formContainer.querySelectorAll('.swatches > li'));
  const total = items.filter((item) => !item.classList.contains('customer-favorites-option-hidden')).length;
  const shown = items.filter((item) => getEligibleConfigurationSwatchInput(item)).length;

  if (activeFilters.length === 0) {
    return (status.getAttribute('data-status-cleared') || '')
      .replaceAll('__TOTAL__', String(total))
      .replaceAll('__SHOWN__', String(shown));
  }

  const filters = Array.from(formContainer.querySelectorAll('.filter-config button[aria-pressed="true"]'))
    .map((button) => button.textContent.trim())
    .filter(Boolean)
    .join(', ');

  return (status.getAttribute('data-status-filtered') || '')
    .replaceAll('__SHOWN__', String(shown))
    .replaceAll('__TOTAL__', String(total))
    .replaceAll('__FILTERS__', filters);
}

function initializeConfigurationFilterRoving(group) {
  const buttons = getConfigurationFilterGroupButtons(group);
  if (buttons.length === 0) {
    return;
  }

  buttons.forEach((filterButton) => {
    filterButton.addEventListener('click', () => {
      toggleConfigurationFilter(filterButton);
    });

    filterButton.addEventListener('keydown', (event) => {
      handleConfigurationFilterKeydown(event, group);
    });
  });
}

function initializeConfigurationFilters() {
  document.querySelectorAll('.filter-config-group').forEach((group) => {
    if (group.dataset.filterGroupInitialized === 'true') {
      return;
    }

    group.dataset.filterGroupInitialized = 'true';
    initializeConfigurationFilterRoving(group);
  });
}

// Hide configuration option labels that are not needed for the product
function hideConfigurationOptionLabels() {
  const configurationOptions = Array.from(document.querySelectorAll('.configuration-items'));

  configurationOptions.forEach(option => {
    const shouldHideSelectedValue =
      option.querySelector('[data-option-name*="Location"]') ||
      option.querySelector('[data-option-name*="Radius"]');

    if (!shouldHideSelectedValue) return;

    // Hide only the redundant selected-value text, not the group label.
    const selectedValueLabel =
      option.querySelector('[id$="product-form__input_color_config_items_update"]') ||
      option.querySelector('label span');

    selectedValueLabel?.classList.add('sr-only');
  });
}
